import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  ChevronLeft, ChevronRight, X, ArrowRight, Sparkles, Users, Coins,
  Globe, ShieldCheck, GraduationCap, Music2, Banknote, Repeat, Crown,
  PiggyBank, TrendingUp, Heart, Trophy, Rocket
} from 'lucide-react';

/* ----------------------------- SLIDES META ----------------------------- */

const SLIDES = [
  { id: 'brand',       bg: 'from-amber-100 via-rose-50 to-sky-100' },
  { id: 'what-is',     bg: 'from-rose-50 via-amber-50 to-orange-100' },
  { id: 'how-it-works',bg: 'from-sky-50 via-emerald-50 to-amber-50' },
  { id: 'features',    bg: 'from-violet-50 via-rose-50 to-amber-50' },
  { id: 'for-schools', bg: 'from-amber-50 via-emerald-50 to-sky-50' },
  { id: 'trust',       bg: 'from-emerald-50 via-sky-50 to-indigo-50' },
  { id: 'cta',         bg: 'from-amber-200 via-rose-100 to-amber-100' },
];

/* ----------------------------- HELPERS ----------------------------- */

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
  center:        { x: 0, opacity: 1 },
  exit:  (d) => ({ x: d > 0 ? '-30%' : '30%', opacity: 0 }),
};

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, delay: 0.15 + delay, ease: [0.32, 0.72, 0, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const ParallaxShapes = ({ direction }) => (
  <>
    <motion.div
      key={`a-${direction}`}
      initial={{ x: direction > 0 ? 200 : -200, opacity: 0 }}
      animate={{ x: 0, opacity: 0.3 }}
      transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
      className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/40 blur-3xl pointer-events-none"
    />
    <motion.div
      key={`b-${direction}`}
      initial={{ x: direction > 0 ? 100 : -100, opacity: 0 }}
      animate={{ x: 0, opacity: 0.2 }}
      transition={{ duration: 0.9, ease: [0.32, 0.72, 0, 1] }}
      className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-white/40 blur-3xl pointer-events-none"
    />
  </>
);

const Frame = ({ direction, children }) => (
  <motion.div
    custom={direction}
    variants={slideVariants}
    initial="enter"
    animate="center"
    exit="exit"
    transition={{ x: { duration: 0.55, ease: [0.32, 0.72, 0, 1] }, opacity: { duration: 0.35 } }}
    className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-16 py-12"
  >
    <ParallaxShapes direction={direction} />
    <div className="relative z-10 max-w-6xl w-full">{children}</div>
  </motion.div>
);

/* ----------------------------- SLIDES ----------------------------- */

function BrandSlide({ direction, navigate }) {
  return (
    <Frame direction={direction}>
      <div className="text-center">
        <Reveal>
          <motion.div
            animate={{ rotate: [0, 8, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-block mb-6"
          >
            <PiggyBank className="w-20 h-20 text-amber-500" />
          </motion.div>
        </Reveal>
        <Reveal delay={0.06}>
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tight text-stone-900 leading-none">
            Save together.
            <br />
            <span className="text-amber-600">Win together.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-8 text-xl sm:text-2xl text-stone-700 max-w-3xl mx-auto">
            The world's most trusted rotational savings platform.
            Join a club, contribute monthly, take your turn winning the pot.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-amber-500 hover:bg-amber-600 text-lg px-8 h-12"
              data-testid="brand-cta-signup"
            >
              Get Started — Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className="text-lg px-8 h-12 border-stone-400"
              data-testid="brand-cta-classic"
            >
              Classic homepage
            </Button>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-12 flex justify-center gap-6 text-sm text-stone-600 flex-wrap">
            <span className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Bank-level security</span>
            <span className="flex items-center gap-1"><Globe className="w-4 h-4 text-sky-600" /> 50+ countries</span>
            <span className="flex items-center gap-1"><Users className="w-4 h-4 text-amber-600" /> 10K+ members</span>
          </div>
        </Reveal>
      </div>
    </Frame>
  );
}

function WhatIsSlide({ direction }) {
  const pillars = [
    { icon: Users, label: 'Group savings', desc: 'You + your friends, family, or coworkers contribute equally each month.' },
    { icon: Repeat, label: 'Take turns', desc: 'Every month, one member receives the full pot.' },
    { icon: Trophy, label: 'Everyone wins', desc: 'By the end, every member has had a turn collecting the lump sum.' },
    { icon: Heart, label: 'Built on trust', desc: 'A 4,000-year-old tradition known as susu, tanda, esusu, or ROSCA — modernized.' },
  ];
  return (
    <Frame direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-rose-700 font-semibold mb-3">
          <Sparkles className="w-5 h-5" /> Slide 02
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-3">
          What is a <span className="text-amber-600">Susu</span>?
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="text-xl text-stone-700 mb-8 max-w-3xl">
          A community savings club. People contribute equally, and each month one person takes the pot.
          By the time the rotation finishes, everyone has won.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {pillars.map((p, i) => (
          <Reveal key={p.label} delay={0.18 + i * 0.07}>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 h-full border border-rose-100">
              <p.icon className="w-8 h-8 text-rose-600 mb-3" />
              <div className="font-bold text-stone-900 text-lg mb-1">{p.label}</div>
              <div className="text-sm text-stone-600 leading-relaxed">{p.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </Frame>
  );
}

function HowItWorksSlide({ direction }) {
  const steps = [
    { icon: Users,   label: 'Create or join a club',   desc: '2 to 200+ members. Set the contribution and cadence.' },
    { icon: Coins,   label: 'Contribute your share',    desc: 'Auto-pay from your Mini Bank, bank, or card.' },
    { icon: Repeat,  label: 'Watch the rotation',       desc: 'Each cycle, one member receives the full pot.' },
    { icon: Trophy,  label: 'Take your turn winning',   desc: 'When it\'s your month, the lump sum hits your wallet.' },
  ];
  return (
    <Frame direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <Rocket className="w-5 h-5" /> Slide 03
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-8">How it works</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <Reveal key={s.label} delay={0.16 + i * 0.08}>
            <div className="relative bg-white/80 backdrop-blur rounded-2xl p-5 h-full">
              <div className="absolute -top-3 -left-3 bg-emerald-500 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold shadow-md">
                {i + 1}
              </div>
              <s.icon className="w-9 h-9 text-emerald-600 mb-3" />
              <div className="font-bold text-stone-900 text-lg mb-1">{s.label}</div>
              <div className="text-sm text-stone-600 leading-relaxed">{s.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.55}>
        <div className="mt-8 bg-emerald-100/70 backdrop-blur rounded-2xl p-5 border border-emerald-200 text-emerald-900 text-center text-lg font-medium">
          No interest. No banks taking a cut. Just neighbors helping neighbors save.
        </div>
      </Reveal>
    </Frame>
  );
}

function FeaturesSlide({ direction }) {
  const features = [
    { icon: Banknote,   color: 'text-emerald-600', name: 'Internal Mini Bank',  desc: 'Available + Protection deposits.' },
    { icon: Repeat,     color: 'text-amber-600',    name: 'Auto-contribution',    desc: 'Schedule once, never miss a round.' },
    { icon: Globe,      color: 'text-sky-600',      name: 'Multi-currency',       desc: 'USD, CAD, NGN, GHS, KES, RWF + more.' },
    { icon: ShieldCheck,color: 'text-violet-600',   name: 'Plaid bank linking',   desc: 'Real-time balance, secure transfers.' },
    { icon: Music2,     color: 'text-rose-600',     name: 'Live music players',   desc: 'Rwandan & Amapiano radio while you save.' },
    { icon: Crown,      color: 'text-indigo-600',   name: 'Premium clubs',        desc: 'Unlimited rotations + priority support.' },
  ];
  return (
    <Frame direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-violet-700 font-semibold mb-3">
          <Sparkles className="w-5 h-5" /> Slide 04
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-2">Built for serious savers</h2>
        <p className="text-xl text-stone-700 mb-8">Everything you need. Nothing you don't.</p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((f, i) => (
          <Reveal key={f.name} delay={0.14 + i * 0.06}>
            <div className="bg-white/80 backdrop-blur rounded-2xl p-5 h-full">
              <f.icon className={`w-10 h-10 mb-3 ${f.color}`} />
              <div className="font-bold text-stone-900 text-lg mb-1">{f.name}</div>
              <div className="text-sm text-stone-600">{f.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </Frame>
  );
}

function ForSchoolsSlide({ direction, navigate }) {
  return (
    <Frame direction={direction}>
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 bg-amber-200/60 text-amber-900 px-3 py-1.5 rounded-full text-sm font-bold mb-4">
              <GraduationCap className="w-4 h-4" /> NEW · School Susu
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
              For classrooms & families.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-xl text-stone-700 mb-6">
              200 kids in a class. Each contributes $1 a day. Every day, one classmate's parent receives
              the pot. By the end of the school year, every kid has had a turn.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <ul className="space-y-2 mb-6">
              {[
                'Kids see virtual coins, badges, and streaks',
                'Real money stays at the parent layer (compliant)',
                'Class leaderboards and 100% participation rewards',
                'Printable pitch document for school boards',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-stone-700">
                  <span className="text-amber-600 font-bold mt-0.5">✓</span> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => navigate('/school-pitch/slides')}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="schools-pitch-slides"
              >
                See the school pitch deck <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/kids')}
                data-testid="schools-kids-hub"
              >
                Open Kids Hub
              </Button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="bg-white/80 backdrop-blur rounded-3xl p-6 shadow-lg">
            <div className="grid grid-cols-3 gap-3 mb-4">
              {['lion', 'panda', 'unicorn', 'robot', 'dragon', 'fox'].map((a, i) => {
                const colors = ['bg-amber-500', 'bg-stone-700', 'bg-pink-500', 'bg-blue-500', 'bg-emerald-500', 'bg-orange-500'];
                return (
                  <motion.div
                    key={a}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4 + i * 0.08, ease: 'backOut' }}
                    className={`${colors[i]} rounded-2xl aspect-square flex items-center justify-center text-white font-bold text-2xl shadow-md`}
                  >
                    {a.charAt(0).toUpperCase()}
                  </motion.div>
                );
              })}
            </div>
            <div className="text-center mt-4">
              <div className="text-sm font-bold text-stone-700 mb-2">CLASS PARTICIPATION</div>
              <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1.4, delay: 0.8, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-emerald-500 to-amber-500"
                />
              </div>
              <div className="text-xs text-emerald-700 font-bold mt-1">100% — every kid saved today!</div>
            </div>
          </div>
        </Reveal>
      </div>
    </Frame>
  );
}

function TrustSlide({ direction }) {
  const partners = [
    { name: 'Stripe',  desc: 'Card payments, PCI Level 1' },
    { name: 'PayPal',  desc: 'International transfers' },
    { name: 'Plaid',   desc: 'Bank linking & verification' },
    { name: 'SendGrid',desc: 'Transactional email' },
  ];
  return (
    <Frame direction={direction}>
      <Reveal>
        <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <ShieldCheck className="w-5 h-5" /> Slide 06
        </div>
      </Reveal>
      <Reveal delay={0.06}>
        <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-3">
          Built on trust. Backed by the giants.
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="text-xl text-stone-700 mb-8 max-w-3xl">
          We don't hold your money in our basement. Real funds flow through bank-grade, regulated infrastructure.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {partners.map((p, i) => (
          <Reveal key={p.name} delay={0.18 + i * 0.08}>
            <div className="bg-white/85 backdrop-blur rounded-2xl p-5 h-full text-center">
              <div className="text-3xl font-bold text-stone-900 mb-2">{p.name}</div>
              <div className="text-sm text-stone-600">{p.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.55}>
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          {[
            { icon: ShieldCheck, label: 'AES-256 encryption' },
            { icon: TrendingUp,  label: 'Real-time bank balance' },
            { icon: Globe,       label: 'GDPR / PIPEDA compliant' },
          ].map((b) => (
            <div key={b.label} className="bg-emerald-100/70 backdrop-blur rounded-xl p-4 flex items-center gap-3">
              <b.icon className="w-6 h-6 text-emerald-700" />
              <span className="font-medium text-emerald-900">{b.label}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </Frame>
  );
}

function FinalCtaSlide({ direction, navigate }) {
  return (
    <Frame direction={direction}>
      <div className="text-center">
        <Reveal>
          <motion.div
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-block mb-6"
          >
            <Coins className="w-20 h-20 text-amber-600" />
          </motion.div>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="text-5xl sm:text-7xl lg:text-8xl font-bold text-stone-900 mb-4 leading-none">
            Your turn
            <br />
            <span className="text-amber-600">to win.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 text-xl sm:text-2xl text-stone-700 max-w-2xl mx-auto">
            Free to join. No credit card. Start your first club in under 2 minutes.
          </p>
        </Reveal>
        <Reveal delay={0.28}>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-amber-500 hover:bg-amber-600 text-xl px-10 h-14 shadow-lg"
              data-testid="final-cta-signup"
            >
              Sign up free <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
              className="text-xl px-10 h-14 border-stone-400"
              data-testid="final-cta-back"
            >
              Browse classic site
            </Button>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <p className="text-xs text-stone-500 mt-12 max-w-md mx-auto">
            Worldwide Susu Money · A modern take on a 4,000-year tradition.
          </p>
        </Reveal>
      </div>
    </Frame>
  );
}

const SLIDE_RENDERERS = [
  BrandSlide, WhatIsSlide, HowItWorksSlide, FeaturesSlide,
  ForSchoolsSlide, TrustSlide, FinalCtaSlide,
];

/* ----------------------------- MAIN PAGE ----------------------------- */

export default function LandingSlides() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = SLIDES.length;

  const goNext = useCallback(() => {
    setDirection(1);
    setIndex(i => Math.min(i + 1, total - 1));
  }, [total]);
  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex(i => Math.max(i - 1, 0));
  }, []);
  const goTo = (i) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); goPrev(); }
      else if (e.key === 'Escape') navigate('/');
      else if (e.key === 'Home') { setDirection(-1); setIndex(0); }
      else if (e.key === 'End')  { setDirection(1);  setIndex(total - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, navigate, total]);

  const current = SLIDES[index];
  const Renderer = SLIDE_RENDERERS[index];

  return (
    <div
      className={`fixed inset-0 overflow-hidden bg-gradient-to-br ${current.bg} transition-colors duration-700`}
      data-testid="landing-slides-container"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 px-4 sm:px-6 py-4 flex justify-between items-center pointer-events-none">
        <div className="flex items-center gap-2 bg-white/60 backdrop-blur px-3 py-1.5 rounded-full shadow-sm pointer-events-auto">
          <PiggyBank className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-semibold text-stone-800">Susu Worldwide</span>
        </div>
        <div className="flex items-center gap-3 pointer-events-auto">
          <span
            className="bg-white/60 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium text-stone-800 shadow-sm"
            data-testid="landing-slide-counter"
          >
            {index + 1} / {total}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/')}
            className="bg-white/70 backdrop-blur border-stone-300 hover:bg-white"
            data-testid="exit-landing-slides-btn"
          >
            <X className="w-4 h-4 mr-1" /> Exit
          </Button>
        </div>
      </div>

      {/* Slide content */}
      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={index}
          custom={direction}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(_, info) => {
            if (info.offset.x < -100 && index < total - 1) goNext();
            else if (info.offset.x > 100 && index > 0) goPrev();
          }}
          className="absolute inset-0"
          data-testid={`landing-slide-${current.id}`}
        >
          <Renderer direction={direction} navigate={navigate} />
        </motion.div>
      </AnimatePresence>

      {/* Left arrow */}
      <motion.button
        whileHover={{ scale: 1.1, x: -3 }}
        whileTap={{ scale: 0.95 }}
        onClick={goPrev}
        disabled={index === 0}
        className={`absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 sm:w-16 sm:h-16 rounded-full backdrop-blur shadow-lg flex items-center justify-center transition-opacity ${
          index === 0 ? 'opacity-30 cursor-not-allowed bg-white/50' : 'bg-white/80 hover:bg-white cursor-pointer'
        }`}
        aria-label="Previous slide"
        data-testid="landing-prev-btn"
      >
        <ChevronLeft className="w-7 h-7 text-stone-900" />
      </motion.button>

      {/* Right arrow */}
      <motion.button
        whileHover={{ scale: 1.1, x: 3 }}
        whileTap={{ scale: 0.95 }}
        onClick={goNext}
        disabled={index === total - 1}
        className={`absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 sm:w-16 sm:h-16 rounded-full backdrop-blur shadow-lg flex items-center justify-center transition-opacity ${
          index === total - 1 ? 'opacity-30 cursor-not-allowed bg-white/50' : 'bg-amber-500 hover:bg-amber-600 cursor-pointer'
        }`}
        aria-label="Next slide"
        data-testid="landing-next-btn"
      >
        <ChevronRight className={`w-7 h-7 ${index === total - 1 ? 'text-stone-900' : 'text-white'}`} />
      </motion.button>

      {/* Dots */}
      <div className="absolute bottom-6 left-0 right-0 z-30 flex justify-center items-center gap-2 px-4">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all rounded-full ${
              i === index ? 'w-8 h-2 bg-amber-600' : 'w-2 h-2 bg-stone-400/60 hover:bg-stone-600'
            }`}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`landing-dot-${i}`}
          />
        ))}
      </div>

      {/* First-slide hint */}
      {index === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-16 left-0 right-0 text-center z-20 pointer-events-none"
        >
          <span className="inline-block bg-stone-900/70 text-white px-4 py-1.5 rounded-full text-xs">
            Click arrows · use ← → keys · or swipe
          </span>
        </motion.div>
      )}
    </div>
  );
}
